<template>
  <div id="SecuritySettings" class="h-screen w-screen flex flex-col overflow-hidden relative security-bg"
    :class="{ 'xl:h-auto xl:w-auto xl:overflow-visible': isDesktop }"
    style="font-family: 'Lato', sans-serif;">

    <!-- Decorative background (mobile only) -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none xl:hidden">
      <div class="absolute -top-20 -right-8 w-56 h-[380px] rotate-[20deg] rounded-[36px] security-deco-top"></div>
      <div class="absolute -bottom-16 -left-12 w-72 h-72 rounded-full security-deco-bottom"></div>
    </div>

    <div class="flex flex-col flex-1 min-h-0 relative z-10" :class="{ 'xl:flex-none': isDesktop }">

      <!-- Header: back + logo (mobile only) -->
      <div v-if="!isDesktop" class="pt-safe flex items-center gap-3 px-safe pb-3 security-header" :class="{ scrolled: isScrolled }">
        <button @click="backTo({ name: 'Profile' })"
          class="w-10 h-10 rounded-xl flex items-center justify-center bg-card security-back-btn active:scale-95 transition-transform duration-150 shrink-0">
          <ArrowLeftIcon class="h-[18px] w-[18px] text-foreground" />
        </button>
        <p class="text-[16px] font-bold text-foreground">{{ $t('profile.security.headline') }}</p>
      </div>

      <!-- Scrollable content -->
      <div class="flex-1 min-h-0 overflow-y-auto pb-safe px-safe-flush security-scroll" :class="{ 'xl:flex-none xl:overflow-visible xl:pb-0 xl:px-0': isDesktop }" @scroll="onScroll">

        <!-- Verification method section -->
        <div class="px-5 mb-4 mt-6">
          <div class="flex items-center gap-2 mb-2 pl-1">
            <div class="w-1 h-3.5 rounded-full" style="background: linear-gradient(180deg, #06C8FF, #009FE3);"></div>
            <h2 class="text-[13px] font-semibold text-muted-foreground">
              {{ $t('profile.security.securityType') }}
            </h2>
          </div>

          <div class="flex flex-col gap-2.5">
            <!-- Biometric -->
            <button @click="setSecurityType('biometric')"
              class="security-option rounded-xl px-4 py-4 w-full text-left active:scale-[0.99] transition-all duration-150"
              :class="{ 'security-option--active': profileStore.securityType === 'biometric' }">
              <div class="flex items-center gap-3.5">
                <div class="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 security-option-icon"
                  :class="{ 'security-option-icon--active': profileStore.securityType === 'biometric' }">
                  <DevicePhoneMobileIcon class="h-[18px] w-[18px]" />
                </div>
                <div class="flex-1 min-w-0">
                  <span class="text-[14px] font-semibold text-foreground block">
                    {{ $t('profile.security.device_verification') }}
                  </span>
                  <span class="text-[12px] text-muted-foreground leading-snug mt-0.5 block">
                    {{ $t('profile.security.device_verification_description') }}
                  </span>
                </div>
                <Transition name="fade">
                  <div v-if="profileStore.securityType === 'biometric'"
                    class="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
                    style="background: rgba(6, 200, 255, 0.12);">
                    <CheckIcon class="h-3.5 w-3.5" style="color: #06C8FF;" />
                  </div>
                </Transition>
              </div>
            </button>

            <!-- Password -->
            <button @click="setSecurityType('password')"
              class="security-option rounded-xl px-4 py-4 w-full text-left active:scale-[0.99] transition-all duration-150"
              :class="{ 'security-option--active': profileStore.securityType === 'password' }">
              <div class="flex items-center gap-3.5">
                <div class="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 security-option-icon"
                  :class="{ 'security-option-icon--active': profileStore.securityType === 'password' }">
                  <LockClosedIcon class="h-[18px] w-[18px]" />
                </div>
                <div class="flex-1 min-w-0">
                  <span class="text-[14px] font-semibold text-foreground block">
                    {{ $t('profile.security.password_verification') }}
                  </span>
                  <span class="text-[12px] text-muted-foreground leading-snug mt-0.5 block">
                    {{ $t('profile.security.password_verification_description') }}
                  </span>
                </div>
                <Transition name="fade">
                  <div v-if="profileStore.securityType === 'password'"
                    class="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
                    style="background: rgba(6, 200, 255, 0.12);">
                    <CheckIcon class="h-3.5 w-3.5" style="color: #06C8FF;" />
                  </div>
                </Transition>
              </div>
            </button>

            <!-- None -->
            <button @click="setSecurityType('none')"
              class="security-option rounded-xl px-4 py-4 w-full text-left active:scale-[0.99] transition-all duration-150"
              :class="{ 'security-option--active': profileStore.securityType === 'none' }">
              <div class="flex items-center gap-3.5">
                <div class="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 security-option-icon"
                  :class="{ 'security-option-icon--active': profileStore.securityType === 'none' }">
                  <LockOpenIcon class="h-[18px] w-[18px]" />
                </div>
                <div class="flex-1 min-w-0">
                  <span class="text-[14px] font-semibold text-foreground block">
                    {{ $t('profile.security.no_verification') }}
                  </span>
                  <span class="text-[12px] text-muted-foreground leading-snug mt-0.5 block">
                    {{ $t('profile.security.no_verification_description') }}
                  </span>
                </div>
                <Transition name="fade">
                  <div v-if="profileStore.securityType === 'none'"
                    class="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
                    style="background: rgba(6, 200, 255, 0.12);">
                    <CheckIcon class="h-3.5 w-3.5" style="color: #06C8FF;" />
                  </div>
                </Transition>
              </div>
            </button>
          </div>
        </div>

      </div>
    </div>

    <!-- Password dialog -->
    <PromptPassword
      :show="profileStore.showPromptPassword === 'new' || profileStore.showPromptPassword === 'confirm'"
      @close="profileStore.closePromptNewPassword()"
      @confirmed="handleNewPassword" :title="promptTitle"
      :error="newPasswordError" :description="promptDescription" />
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed, ref } from 'vue';
import { useProfileStore, SecurityType } from '../../stores/profileStore';
import { ArrowLeftIcon, CheckIcon, DevicePhoneMobileIcon, LockClosedIcon, LockOpenIcon } from '@heroicons/vue/24/outline';
import PromptPassword from '../../components/PasswordPrompt.vue';
import { useI18n } from 'vue-i18n';
import { useAppRouter } from '../../composables/useAppRouter';
import { useIsDesktop } from '../../composables/useIsDesktop';

const { t } = useI18n();
const { backTo } = useAppRouter();
const isDesktop = useIsDesktop();
const profileStore = useProfileStore();

const isScrolled = ref(false);
function onScroll(e: Event) {
  isScrolled.value = (e.target as HTMLElement).scrollTop > 8;
}

const newPasswordError = ref<string | null>(null);

const setSecurityType = async (type: SecurityType) => {
  await profileStore.setSecurityType(type);
};

const handleNewPassword = async (password: string) => {
  newPasswordError.value = null;
  await profileStore.setPassword(password).catch(err => {
    let message = err.message;
    let text = '';

    switch (message) {
      case 'promptpassword.confirm.attempts_exceeded':
        text = t(message, { attempts: profileStore.passwordConfirmAttempts, maxAttempts: profileStore.passwordMaxAttempts });
        break;
      case 'promptpassword.confirm.mismatch':
        text = t(message, { attempts: profileStore.passwordConfirmAttempts, maxAttempts: profileStore.passwordMaxAttempts });
        break;
      default:
        text = t(message as string);
    }

    newPasswordError.value = text;
  });
};

const promptTitle = computed(() => {
  switch (profileStore.showPromptPassword) {
    case 'new':
      return t('promptpassword.new.title');
    case 'confirm':
      return t('promptpassword.confirm.title');
    default:
      return '';
  }
});

const promptDescription = computed(() => {
  switch (profileStore.showPromptPassword) {
    case 'new':
      return t('promptpassword.new.description');
    case 'confirm':
      return t('promptpassword.confirm.description');
    default:
      return '';
  }
});

onMounted(async () => {
  await profileStore.load();
});
</script>

<style scoped>
/* Light mode */
.security-header {
  border-bottom: 1px solid transparent;
  transition: border-color 0.2s ease, backdrop-filter 0.2s ease;
}
.security-header.scrolled {
  border-bottom-color: rgba(0, 0, 0, 0.06);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}
.security-bg {
  background: linear-gradient(175deg, #FFFFFF 0%, #F0F7FA 40%, #F4F4F4 100%);
}
@media (min-width: 1280px) {
  .security-bg { background: transparent; }
}
.security-deco-top {
  background: linear-gradient(180deg, rgba(6, 200, 255, 0.09) 0%, transparent 55%);
}
.security-deco-bottom {
  background: radial-gradient(circle, rgba(6, 200, 255, 0.06) 0%, transparent 60%);
}
.security-back-btn {
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}
.security-option {
  background: #FFFFFF;
  border: 1.5px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}
.security-option--active {
  border-color: rgba(6, 200, 255, 0.30);
  box-shadow: 0 2px 12px rgba(6, 200, 255, 0.10);
}
.security-option-icon {
  background: rgba(0, 0, 0, 0.04);
  color: var(--muted-foreground);
}
.security-option-icon--active {
  background: rgba(6, 200, 255, 0.10);
  color: #06C8FF;
}
.security-scroll {
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}
.security-scroll::-webkit-scrollbar {
  display: none;
}

/* Dark mode */
:is(.dark *).security-header.scrolled {
  border-bottom-color: rgba(255, 255, 255, 0.06);
}
:is(.dark *).security-bg {
  background: linear-gradient(175deg, #1B1C1E 0%, #1e2528 40%, #1B1C1E 100%);
}
@media (min-width: 1280px) {
  :is(.dark *).security-bg { background: transparent; }
}
:is(.dark *).security-deco-top {
  background: linear-gradient(180deg, rgba(6, 200, 255, 0.15) 0%, transparent 55%);
}
:is(.dark *).security-deco-bottom {
  background: radial-gradient(circle, rgba(6, 200, 255, 0.04) 0%, transparent 50%);
}
:is(.dark *).security-back-btn {
  border: 1px solid rgba(6, 200, 255, 0.10);
  box-shadow: 0 1px 6px rgba(6, 200, 255, 0.05);
}
:is(.dark *).security-option {
  background: #1E2024;
  border: 1.5px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}
:is(.dark *).security-option--active {
  border-color: rgba(6, 200, 255, 0.25);
  box-shadow: 0 2px 12px rgba(6, 200, 255, 0.08);
}
:is(.dark *).security-option-icon {
  background: rgba(255, 255, 255, 0.06);
}
:is(.dark *).security-option-icon--active {
  background: rgba(6, 200, 255, 0.12);
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
